import { cn } from '@/shared/lib/cn';

export interface LogoProps {
  /** Pixel size of the mark. The wordmark scales from it. */
  size?: number;
  /** `mark` is the glyph alone; `full` adds the Novix wordmark beside it. */
  variant?: 'mark' | 'full';
  /**
   * `brand` paints the gradient (use on light surfaces); `on-gradient` renders
   * the mark in the on-gradient ink so it stays legible on the brand band.
   */
  tone?: 'brand' | 'on-gradient';
  className?: string;
  /**
   * Accessible name. Pass `''` when the logo sits next to a visible "Novix"
   * heading, so a screen reader does not read the brand twice.
   */
  title?: string;
}

/**
 * The Novix mark.
 *
 * ONE component, deliberately: every screen goes through here, so changing the
 * artwork is a single-file edit rather than a hunt through the auth flow. That
 * paid for itself when the mark was revised — only this file, the favicon and
 * design/logo/novix-mark.svg had to move, and those three are named in each
 * other's comments precisely because they are the copies that can drift.
 * Inlined as JSX rather than an <img src="…svg"> so it can inherit the current
 * colour and so it needs no extra network round-trip on the login screen — the
 * first thing a cold visitor sees.
 *
 * Source: design/logo/novix-mark.svg — a stroked "N" whose two uprights end in
 * solid arrowheads: the left one points down, the right one points up. The
 * letter carries the two directions money moves, which is the whole product.
 *
 * The uprights are deliberately SHORTER than the arrowheads' reach (11→31 and
 * 37→17, against tips at y=43 and y=5). The stroke stops where the triangle's
 * base begins so the head sits ON the end of the line rather than overlapping
 * it — overlapping reads as a blob at 16px, which is where a favicon lives.
 */
export function Logo({
  size = 40,
  variant = 'mark',
  tone = 'brand',
  className,
  title = 'Novix'
}: LogoProps) {
  const gradientId = 'novix-logo-gradient';
  const labelled = title !== '';
  const paint = tone === 'brand' ? `url(#${gradientId})` : 'currentColor';

  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <svg
        viewBox="0 0 48 48"
        width={size}
        height={size}
        className="shrink-0"
        {...(labelled ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
      >
        {tone === 'brand' && (
          <defs>
            <linearGradient
              id={gradientId}
              x1="4"
              y1="4"
              x2="44"
              y2="44"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#0078ca" />
              <stop offset="1" stopColor="#3bd4d4" />
            </linearGradient>
          </defs>
        )}
        <g fill="none" stroke={paint} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 11 V 31" />
          <path d="M11 11 L 37 37" />
          <path d="M37 37 V 17" />
        </g>
        <g fill={paint}>
          <path d="M11 43 L 2.5 30 L 19.5 30 Z" />
          <path d="M37 5 L 45.5 18 L 28.5 18 Z" />
        </g>
      </svg>

      {variant === 'full' && (
        <span
          aria-hidden
          className="font-semibold tracking-tight"
          style={{ fontSize: size * 0.62, lineHeight: 1 }}
        >
          Novix
        </span>
      )}
    </span>
  );
}
